import { useThree } from "@react-three/fiber";
import { Container, type FontFamilies } from "@react-three/uikit";
import {
  Children,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { CRTContext, useAudio, VirtualPointerEventsContext } from "../../hooks";
import { ChannelOverlay } from "./ChannelOverlay";
import { CRTEffect } from "./CRTEffect";
import { CRTFrame } from "./CRTFrame";
import { CRTUIRenderer } from "./CRTUIRenderer";
import { ScreenBackground } from "./ScreenBackground";
import {
  type ChannelAudioConfig,
  ScreenChannel,
  type ScreenChannelProps,
} from "./ScreenChannel";
import { ScreenContent } from "./ScreenContent";
import { ScreenEmissionLight } from "./ScreenEmissionLight";
import { ScreenFooter } from "./ScreenFooter";
import { VolumeOverlay } from "./VolumeOverlay";

/** Methods exposed via ref for external control (e.g., mobile overlay) */
export interface ScreenRootHandle {
  volumeUp: () => void;
  volumeDown: () => void;
  channelUp: () => void;
  channelDown: () => void;
}

/** Audio configuration for root-level (channel-wide) audio */
export interface RootAudioConfig {
  /** Audio source URL (supports mp3, wav, ogg, etc.) */
  src: string;
  /** Whether to loop the audio (default: true for root audio) */
  loop?: boolean;
  /** Base volume level 0-1, combined with TV volume control (default: 1) */
  volume?: number;
}

export interface ScreenRootProps {
  children: ReactNode;
  scene: THREE.Group;
  fontFamily?: string;
  fontFamilies?: FontFamilies;
  /** Fixed dimensions for the CRT screen content (default: 1920x1080) */
  width?: number;
  height?: number;
  /** Physical geometry curvature for CRT glass effect (default: 0.3) */
  geometryCurvature?: number;
  /** Scale of the TV model (default: 6) */
  scale?: number;
  /** Position of the TV in the scene */
  position?: [number, number, number];
  /** Rotation of the TV in the scene */
  rotation?: [number, number, number];
  /** Whether the TV is powered on (default: true). Animates on/off transition */
  isPoweredOn?: boolean;
  /** Duration of power ON transition in ms (default: 2000) */
  powerOnDuration?: number;
  /** Duration of power OFF transition in ms (default: 800) */
  powerOffDuration?: number;
  /** Called when the power button on the TV frame is pressed */
  onPowerToggle?: () => void;
  /** Show control panel on TV frame (default: true) */
  showControlPanel?: boolean;
  /**
   * Background audio that plays across all channels.
   * Can be a URL string or a config object for more control.
   * Audio respects the TV's volume control and power state.
   *
   * @example
   * ```tsx
   * // Simple usage - plays on loop by default
   * <CRTScreen.Root audio="/sounds/tv-hum.mp3">
   *
   * // With config
   * <CRTScreen.Root audio={{ src: "/sounds/ambient.mp3", loop: true, volume: 0.5 }}>
   * ```
   */
  audio?: string | RootAudioConfig;
}

/**
 * CRTScreen.Root - Main container for the CRT screen system
 *
 * Must be placed inside a React Three Fiber Canvas.
 * Automatically detects and recomposes CRTScreen.Background, CRTScreen.Content,
 * and CRTScreen.Footer children into the correct render hierarchy.
 *
 * Accepts a ref that exposes control methods for external use (e.g., mobile overlay).
 *
 * @example
 * ```tsx
 * <Canvas>
 *   <CRTScreen.Root fontFamily="PressStart2P" settings={...}>
 *     <CRTScreen.Background>
 *       <MyCustomBackground />
 *     </CRTScreen.Background>
 *     <CRTScreen.Content>
 *       <MyUIContent />
 *     </CRTScreen.Content>
 *     <CRTScreen.Footer>
 *       <MyFooterContent />
 *     </CRTScreen.Footer>
 *   </CRTScreen.Root>
 * </Canvas>
 * ```
 */
export const ScreenRoot = forwardRef(function ScreenRoot(
  {
    children,
    fontFamily,
    fontFamilies,
    scene,
    width = 1920,
    height = 1080,
    geometryCurvature = 0.3,
    scale = 6,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    isPoweredOn = true,
    powerOnDuration = 3000,
    powerOffDuration = 800,
    onPowerToggle,
    showControlPanel = true,
    audio,
  }: ScreenRootProps,
  ref: ForwardedRef<ScreenRootHandle>,
) {
  const { gl } = useThree();

  // === Power State Animation ===
  const [powerState, setPowerState] = useState(isPoweredOn ? 1 : 0);
  const [powerDirection, setPowerDirection] = useState<1 | -1>(1); // 1 = turning on, -1 = turning off

  // === Volume State ===
  const [volume, setVolume] = useState(20); // 0-40
  const [volumeVisible, setVolumeVisible] = useState(false);

  const handleVolumeUp = useCallback(() => {
    setVolume((v) => Math.min(40, v + 1));
    setVolumeVisible(true);
  }, []);

  const handleVolumeDown = useCallback(() => {
    setVolume((v) => Math.max(0, v - 1));
    setVolumeVisible(true);
  }, []);

  const handleVolumeHide = useCallback(() => {
    setVolumeVisible(false);
  }, []);

  // === Channel State ===
  const [currentChannel, setCurrentChannel] = useState(1);
  const [channelVisible, setChannelVisible] = useState(false);
  const [channels, setChannels] = useState<
    {
      channel: number;
      name?: string;
      noSignal?: boolean;
      audio?: string | ChannelAudioConfig;
    }[]
  >([]);
  const [channelSwitchFlicker, setChannelSwitchFlicker] = useState(0);
  const flickerAnimationRef = useRef<number | null>(null);

  // Trigger channel switch flicker animation
  const triggerChannelFlicker = useCallback(() => {
    // Cancel any existing animation
    if (flickerAnimationRef.current) {
      cancelAnimationFrame(flickerAnimationRef.current);
    }

    const flickerDuration = 250; // ms
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / flickerDuration, 1);

      // Flicker pattern: quick burst at start, then fade out
      // Peak intensity around 30% through, then decay
      let intensity: number;
      if (progress < 0.3) {
        // Ramp up quickly with some randomness
        intensity = (progress / 0.3) * (0.8 + Math.random() * 0.2);
      } else {
        // Decay with slight jitter
        const decay = 1 - (progress - 0.3) / 0.7;
        intensity = decay * decay * (0.6 + Math.random() * 0.2);
      }

      setChannelSwitchFlicker(intensity);

      if (progress < 1) {
        flickerAnimationRef.current = requestAnimationFrame(animate);
      } else {
        setChannelSwitchFlicker(0);
        flickerAnimationRef.current = null;
      }
    };

    flickerAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  const handleChannelUp = useCallback(() => {
    if (channels.length === 0) return;
    triggerChannelFlicker();
    setCurrentChannel((current) => {
      const currentIndex = channels.findIndex((c) => c.channel === current);
      const nextIndex = (currentIndex + 1) % channels.length;
      const nextChannel = channels[nextIndex];
      return nextChannel?.channel ?? current;
    });
    setChannelVisible(true);
  }, [channels, triggerChannelFlicker]);

  const handleChannelDown = useCallback(() => {
    if (channels.length === 0) return;
    triggerChannelFlicker();
    setCurrentChannel((current) => {
      const currentIndex = channels.findIndex((c) => c.channel === current);
      const prevIndex =
        currentIndex <= 0 ? channels.length - 1 : currentIndex - 1;
      const prevChannel = channels[prevIndex];
      return prevChannel?.channel ?? current;
    });
    setChannelVisible(true);
  }, [channels, triggerChannelFlicker]);

  const handleChannelHide = useCallback(() => {
    setChannelVisible(false);
  }, []);

  // Expose control methods via ref for external use (e.g., mobile overlay)
  useImperativeHandle(
    ref,
    () => ({
      volumeUp: handleVolumeUp,
      volumeDown: handleVolumeDown,
      channelUp: handleChannelUp,
      channelDown: handleChannelDown,
    }),
    [handleVolumeUp, handleVolumeDown, handleChannelUp, handleChannelDown],
  );

  const powerAnimationRef = useRef<number | null>(null);
  const lastPowerTargetRef = useRef(isPoweredOn);

  useEffect(() => {
    // Only animate if the target changed
    if (lastPowerTargetRef.current === isPoweredOn) return;
    lastPowerTargetRef.current = isPoweredOn;

    const targetState = isPoweredOn ? 1 : 0;
    const startState = powerState;
    const startTime = performance.now();

    // Set direction for shader
    setPowerDirection(isPoweredOn ? 1 : -1);

    // Cancel any existing animation
    if (powerAnimationRef.current) {
      cancelAnimationFrame(powerAnimationRef.current);
    }

    // Use different durations for on vs off
    const duration = isPoweredOn ? powerOnDuration : powerOffDuration;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for more natural feel
      const eased = isPoweredOn
        ? // Turn on: slow start, fast middle, slow end
          progress < 0.5
          ? 2 * progress * progress
          : 1 - (-2 * progress + 2) ** 2 / 2
        : // Turn off: fast collapse
          1 - (1 - progress) ** 3;

      const newState = startState + (targetState - startState) * eased;
      setPowerState(newState);

      if (progress < 1) {
        powerAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    powerAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (powerAnimationRef.current) {
        cancelAnimationFrame(powerAnimationRef.current);
      }
    };
  }, [isPoweredOn, powerOnDuration, powerOffDuration]);

  // Cleanup flicker animation on unmount
  useEffect(() => {
    return () => {
      if (flickerAnimationRef.current) {
        cancelAnimationFrame(flickerAnimationRef.current);
      }
    };
  }, []);

  // === Context Setup (from CRTRoot) ===
  const pointerStore = VirtualPointerEventsContext.useStore();
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: -9999, y: -9999 });

  const dimensions = useMemo(() => ({ width, height }), [width, height]);

  const virtualPointerEventsValue = useMemo(
    () =>
      VirtualPointerEventsContext.createContextValue(
        pointerStore,
        mousePos,
        canvasContainerRef,
      ),
    [mousePos, pointerStore],
  );

  // UV-based pointer handlers
  const handlePointerMoveUV = useCallback(
    (u: number, v: number) => {
      const x = u * dimensions.width;
      const y = (1 - v) * dimensions.height;
      setMousePos({ x, y });
      virtualPointerEventsValue.updateMousePosition(x, y);
    },
    [dimensions.width, dimensions.height, virtualPointerEventsValue],
  );

  const handlePointerClickUV = useCallback(
    (u: number, v: number) => {
      const x = u * dimensions.width;
      const y = (1 - v) * dimensions.height;
      virtualPointerEventsValue.onClick(x, y);
    },
    [dimensions.width, dimensions.height, virtualPointerEventsValue],
  );

  const handlePointerLeave = useCallback(() => {
    setMousePos({ x: -9999, y: -9999 });
    virtualPointerEventsValue.clearHover();
  }, [virtualPointerEventsValue]);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = canvasContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setMousePos({ x, y });
      virtualPointerEventsValue.updateMousePosition(x, y);
    },
    [virtualPointerEventsValue],
  );

  const onMouseLeave = useCallback(() => {
    setMousePos({ x: -9999, y: -9999 });
    virtualPointerEventsValue.clearHover();
  }, [virtualPointerEventsValue]);

  useMemo(() => {
    virtualPointerEventsValue.setScreenDimensions(width, height);
  }, [virtualPointerEventsValue, width, height]);

  const crtContextValue = useMemo(
    () =>
      CRTContext.createContextValue(
        virtualPointerEventsValue,
        dimensions,
        onMouseMove,
        onMouseLeave,
        { fontFamily, fontFamilies },
      ),
    [
      virtualPointerEventsValue,
      dimensions,
      fontFamily,
      fontFamilies,
      onMouseMove,
      onMouseLeave,
    ],
  );

  const virtualPointerEventsContext = useMemo(
    () => ({ ...virtualPointerEventsValue, onMouseMove, onMouseLeave }),
    [virtualPointerEventsValue, onMouseMove, onMouseLeave],
  );

  const crtContext = useMemo(
    () => ({
      ...crtContextValue,
      virtualPointerEvents: virtualPointerEventsContext,
      _internal: {
        onPointerMoveUV: handlePointerMoveUV,
        onPointerClickUV: handlePointerClickUV,
        onPointerLeave: handlePointerLeave,
      },
    }),
    [
      crtContextValue,
      virtualPointerEventsContext,
      handlePointerMoveUV,
      handlePointerClickUV,
      handlePointerLeave,
    ],
  );

  // === Child Detection and Recomposition ===
  const { backgroundContent, mainContent, footerContent, channelList } =
    useMemo(() => {
      const backgrounds: ReactNode[] = [];
      const content: ReactNode[] = [];
      const footers: ReactNode[] = [];
      const channelData: {
        channel: number;
        name?: string;
        children: ReactNode;
        noSignal?: boolean;
        noOverlay?: boolean;
        audio?: string | ChannelAudioConfig;
      }[] = [];

      Children.forEach(children, (child) => {
        if (isValidElement(child)) {
          if (child.type === ScreenBackground) {
            backgrounds.push(child);
          } else if (child.type === ScreenContent) {
            content.push(child);
          } else if (child.type === ScreenFooter) {
            footers.push(child);
          } else if (child.type === ScreenChannel) {
            // Extract channel data
            const props = child.props as ScreenChannelProps;
            channelData.push({
              channel: props.channel,
              // If noSignal is true, default name to "No Signal"
              name: props.noSignal ? (props.name ?? "No Signal") : props.name,
              children: props.children,
              noSignal: props.noSignal,
              noOverlay: props.noOverlay,
              audio: props.audio,
            });
          } else {
            // Unknown children go to main content
            content.push(child);
          }
        } else {
          content.push(child);
        }
      });

      return {
        backgroundContent: backgrounds.length > 0 ? backgrounds : undefined,
        mainContent: content,
        footerContent: footers.length > 0 ? footers : undefined,
        channelList: channelData,
      };
    }, [children]);

  // Update channels list when children change
  useEffect(() => {
    if (channelList.length > 0) {
      setChannels(
        channelList.map((c) => ({
          channel: c.channel,
          name: c.name,
          noSignal: c.noSignal,
          audio: c.audio,
        })),
      );
      // Set initial channel to first channel if current is not in list
      const channelNumbers = channelList.map((c) => c.channel);
      const firstChannel = channelList[0];
      if (!channelNumbers.includes(currentChannel) && firstChannel) {
        setCurrentChannel(firstChannel.channel);
      }
    }
  }, [channelList, currentChannel]);

  // Get active channel content (or check if it's an empty channel)
  const { activeChannelContent, isEmptyChannel, isNoSignal, isNoOverlay } =
    useMemo(() => {
      if (channelList.length === 0)
        return {
          activeChannelContent: null,
          isEmptyChannel: false,
          isNoSignal: false,
          isNoOverlay: false,
        };
      const activeChannel = channelList.find(
        (c) => c.channel === currentChannel,
      );
      if (!activeChannel)
        return {
          activeChannelContent: null,
          isEmptyChannel: false,
          isNoSignal: false,
          isNoOverlay: false,
        };
      // Check if channel has no children (empty channel)
      const hasContent = activeChannel.children != null;
      return {
        activeChannelContent: activeChannel.children ?? null,
        isEmptyChannel: !hasContent,
        isNoSignal: activeChannel.noSignal ?? false,
        isNoOverlay: activeChannel.noOverlay ?? false,
      };
    }, [channelList, currentChannel]);

  // Get current channel name for overlay
  const currentChannelName = useMemo(() => {
    const channel = channels.find((c) => c.channel === currentChannel);
    return channel?.name;
  }, [channels, currentChannel]);

  // Get active channel's audio config
  const activeChannelAudio = useMemo(() => {
    const channel = channels.find((c) => c.channel === currentChannel);
    return channel?.audio;
  }, [channels, currentChannel]);

  // === Audio Integration ===
  // Root audio plays across all channels when TV is on
  useAudio({
    audio,
    isActive: true,
    tvVolume: volume,
    tvMaxVolume: 40,
    isPoweredOn,
    defaultLoop: true, // Root audio loops by default
  });

  // Channel audio plays only for the active channel
  useAudio({
    audio: activeChannelAudio,
    isActive: true, // Active channel is already determined by activeChannelAudio
    tvVolume: volume,
    tvMaxVolume: 40,
    isPoweredOn,
    defaultLoop: false, // Channel audio doesn't loop by default
  });

  // === Render Target Setup ===
  const renderTarget = useMemo(() => {
    const target = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
    });
    return target;
  }, [width, height]);

  useEffect(() => {
    return () => {
      renderTarget.dispose();
    };
  }, [renderTarget]);

  // === Render ===
  return (
    <VirtualPointerEventsContext.Provider value={virtualPointerEventsContext}>
      <CRTContext.Provider value={crtContext}>
        <CRTFrame
          scene={scene}
          position={position}
          rotation={rotation}
          scale={scale}
          isPoweredOn={isPoweredOn}
          onPowerPress={onPowerToggle}
          onVolumeUpPress={handleVolumeUp}
          onVolumeDownPress={handleVolumeDown}
          onChannelUpPress={handleChannelUp}
          onChannelDownPress={handleChannelDown}
          showControlPanel={showControlPanel}
        >
          <CRTUIRenderer
            renderTarget={renderTarget}
            width={width}
            height={height}
            threeContent={backgroundContent}
            fontFamily={fontFamily}
            fontFamilies={fontFamilies}
          >
            {/* Black background for empty channels */}
            {isEmptyChannel && (
              <Container
                width="100%"
                height="100%"
                backgroundColor="#000000"
                positionType="absolute"
                positionTop={0}
                positionLeft={0}
              />
            )}
            {/* Render active channel content OR main content if no channels */}
            {activeChannelContent ?? mainContent}
            {footerContent}
            <VolumeOverlay
              volume={volume}
              visible={volumeVisible}
              onHide={handleVolumeHide}
            />
            {!isNoOverlay && (
              <ChannelOverlay
                channel={currentChannel}
                channelName={currentChannelName}
                visible={channelVisible}
                onHide={handleChannelHide}
                persistent={isNoSignal}
              />
            )}
          </CRTUIRenderer>

          <CRTEffect
            sourceTexture={renderTarget.texture}
            width={width}
            height={height}
            curvature={geometryCurvature}
            renderer={gl}
            powerState={powerState}
            powerDirection={powerDirection}
            channelSwitchFlicker={channelSwitchFlicker}
            onPointerMoveUV={handlePointerMoveUV}
            onPointerClickUV={handlePointerClickUV}
            onPointerLeave={handlePointerLeave}
          />

          <ScreenEmissionLight
            renderTarget={renderTarget}
            renderer={gl}
            powerState={powerState}
          />
        </CRTFrame>
      </CRTContext.Provider>
    </VirtualPointerEventsContext.Provider>
  );
});
